import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, DoCheck, ElementRef, EventEmitter, HostBinding, inject, Input, OnDestroy, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { CustomerExperienceCategoriesService, CustomerExperienceCategory, SystemSettingType } from '@core/api';
import { getSystemSettingValue } from '@core/store';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { replaceTurkishCharacters } from '@shared/utils';
import { Subject } from 'rxjs';

@Component({
  selector: 'net-select-customer-experience-categories',
  templateUrl: './select-customer-experience-categories.component.html',
  styleUrls: ['./select-customer-experience-categories.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectCustomerExperienceCategoriesComponent,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule
  ]
})
export class SelectCustomerExperienceCategoriesComponent implements OnInit, OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<string | string[] | null> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-categories';
  describedBy = '';
  stateChanges = new Subject<void>();

  categories: CustomerExperienceCategory[] = [];
  filteredCategories: CustomerExperienceCategory[] = [];
  selectCtrl = new UntypedFormControl();
  searchText = '';

  mainCategoryUsage: boolean;

  @Input() multiple: boolean;
  @Input() mode: 'add' | 'edit';
  @Input() defaultSelected = true;
  @Input() searchable = true;
  @Input() tabIndex;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.selectCtrl.disable();
    } else {
      this.selectCtrl.enable();
    }

    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): string | string[] | null { return this.selectCtrl.value; }
  set value(value: string | string[] | null) {
    this.selectCtrl.setValue(value);
    this.onChange(value);
    this.stateChanges.next();
  }
  private _value: CustomerExperienceCategory[];

  @Output() dropdownValues: EventEmitter<CustomerExperienceCategory[]> = new EventEmitter<CustomerExperienceCategory[]>();

  @Output() isExistDisableValue: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('categoriesSelect') categoriesSelect: MatSelect;

  @HostBinding('id') id = `select-categories-${SelectCustomerExperienceCategoriesComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return this.focused || !this.empty; }

  get empty() { return !this.value; }

  private initDefaultOptions(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.categoriesService.search({
        page: 1,
        pageSize: 1000,
        orderBy: 'name',
        orderType: 'ASC',
        filter: {
        }
      })
        .toPromise()
        .then((response) => {
          this.categories = response.data.results;
          this.filteredCategories = response.data.results;

          this.dropdownValues.emit(this.categories);

          this.isExistDisableValueCheck(); // Check if there is any disabled value in the list

          if (this.value?.length > 0) {
            return;
          }

          if (this.defaultSelected && this.filteredCategories.length === 1) {

            this.value = this.filteredCategories.map(c => c.cxCategoryId);

          }

        })
        .finally(() => resolve());
    });
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private categoriesService: CustomerExperienceCategoriesService,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private store: Store,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // Material form field implementation
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.selectCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isExistDisableValueCheck();
    });

    this.store.select(getSystemSettingValue(SystemSettingType.CX_MAIN_CATEGORY_USAGE))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.mainCategoryUsage = value as boolean);
  }
  ngOnInit() {
    this.initDefaultOptions();
  }

  selectAll() {

    if (!this.multiple) {
      return;
    }

    this.selectCtrl.patchValue(this.filteredCategories.map(category => category.cxCategoryId));
    this.value = this.filteredCategories.map(c => c.cxCategoryId);

  }

  deselectAll() {

    if (!this.multiple) {
      return;
    }

    this.selectCtrl.patchValue(null);
    this.value = [];
  }

  onKey(event) {
    if (!this.multiple && !this.searchable) {
      return;
    }
    const selectedCategories = Array.isArray(this.value) ?
      this.getFilteredList(!!event.target.value ? this.filteredCategories : this.categories).filter(category =>
        (this.value as CustomerExperienceCategory[]).map(cat => cat.cxCategoryId).includes(category.cxCategoryId)) : [];
    this.filteredCategories = [...new Set([
      ...this.search(
        this.getFilteredList(!!event.target.value ? this.filteredCategories : this.categories),
        'name',
        event.target.value
      ), ...selectedCategories])];

  }

  search(searchList: any[], searchKey: string, searchText: string) {
    const filter = replaceTurkishCharacters(searchText).toUpperCase();
    return searchList?.filter(option => option[searchKey].toUpperCase().includes(filter));
  }

  openedChange(isOpened: boolean) {
    if (isOpened) {
      const panel = this.categoriesSelect.panel.nativeElement;
      panel.scrollTop = 0;
    }
  }

  onSelectionChange() {
    this.onChange(this.value);
    this.stateChanges.next();
  }

  getFilteredList(list: CustomerExperienceCategory[] = this.filteredCategories) {
    return list.filter(category => {
      if (this.mode === 'add') {
        return category.activeFlag;
      } else {
        return (this.multiple ? this.selectCtrl.value?.includes(category.cxCategoryId) :
          this.selectCtrl.value === category.cxCategoryId) || category.activeFlag;
      }
    });
  }

  isExistDisableValueCheck() {

    const disableItems = this.filteredCategories.filter(category => !category.activeFlag);

    this.isExistDisableValue.emit(
      disableItems.filter(category => {
        if (this.multiple && this.selectCtrl.value?.includes(category.cxCategoryId)) {
          return true;
        } else if (!this.multiple && this.selectCtrl.value === category.cxCategoryId) {
          return true;
        } else {
          return false;
        }
      }).length > 0
    );
  }

  getSelectedValues(): string {
    const controllers = Array.isArray(this.selectCtrl.value) ? this.selectCtrl.value : [this.selectCtrl.value];

    return this.categories
      .filter(category => controllers.includes(category.cxCategoryId))
      .map(category => category.name)
      .join(', ');
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  compareWith = (one: string | string, two: string | string) =>
    one && two && one === two
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');

  writeValue(value: string | string[]) {
    this.value = value;
  }

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }

}
